/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	ReactFlow,
	applyNodeChanges,
	applyEdgeChanges,
	Background,
	BackgroundVariant,
	Connection,
	Controls,
	Edge,
	Node,
} from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";

import "@xyflow/react/dist/style.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useLocalStorageState from "use-local-storage-state";

import { faX } from "@fortawesome/pro-regular-svg-icons";
import {
	isGraphConnected,
	analyzeDisconnectedNodes,
	nodesContainSignatureVariable,
	collectInvalidVariableErrors,
	newStepDataFor,
} from "@signalist/shared-types";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";
import { useGetSendDelayDefaultsQuery } from "@/pages/Private/redux/copywritingSettings/copywritingSettings.api";
import { sendDelayDefaultsSelector } from "@/pages/Private/redux/copywritingSettings/copywritingSettings.slice";
import { DEFAULT_NEW_STEP_DAYS } from "@/utils/stepDelayDefaults";

import {
	EmailNodeConfig,
	LinkedinInmailNodeConfig,
	LinkedinInviteNodeConfig,
	LinkedinNodeConfig,
} from "@/pages/Private/pages/List/components/CampaignOverview/components/nodes";
import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components/Modals/Modal";
import { autoLayoutHorizontal } from "@/pages/Private/pages/List/components/CampaignOverview/utils/autoLayoutHorizontal";
import { hydrateCampaignGraph } from "@/pages/Private/pages/List/components/CampaignOverview/utils/hydrateCampaignGraph.util";
import { edgeTypes } from "@/pages/Private/pages/List/components/CampaignOverview/config/edge-types";
import {
	CampaignNodeTypes,
	handleAddStepProps,
	nodeTypes,
} from "@/pages/Private/pages/List/components/CampaignOverview/config/node-types";

import { classNames } from "@/utils/classNames";

import { CampaignEmailSubjectEditor } from "@/pages/Private/pages/List/components/CampaignOverview/components/CampaignEmailSubjectEditor";
import { setSubjectOnNodeData } from "@/pages/Private/pages/List/components/CampaignOverview/utils/setSubjectOnNodeData";
import { EmailEditor } from "@/pages/Private/pages/Account/components/EmailEditor";

import { ButtonColor } from "@/components/Button/types";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { GetEmailInformation } from "@/enum/list.enum";
import { getSuggestions } from "@/pages/Private/pages/List/utils/column-utils";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useGlobalNodesData } from "../../../List/components/CampaignOverview/components/NodesContextProvider";
import { CopywritingCampaign } from "../../../List/pages/ListEditCopywriting/ListEditCopywriting";
import { useNewNodeConditionModal } from "../../../List/components/CampaignOverview/utils/useNewNodeConditionModal";
import {
	buildConditionalValue,
	ConditionalNodeKind,
} from "../../../List/components/CampaignOverview/utils/buildConditionalValue";
import { resolveNewStepDays } from "../../../List/components/CampaignOverview/utils/resolveNewStepDays";
import { ListPerson } from "../../../List/schema/list";
import { LinkedinEditor } from "../../../Account/components/LinkedinEditor";
import { NodeData } from "../../../List/components/CampaignOverview/pages/CampaignPage";
import { Sender } from "../../../List/components/CampaignOverview/components/Sender";

import { LinkedinVisitNodeConfig } from "../../../List/components/CampaignOverview/components/nodes/LinkedinVisitNode";
import { resolveEffectiveSenders } from "../../../List/components/CampaignOverview/utils/resolveEffectiveSenders.util";
import { AiSnippets, AiSnippetsHandle } from "../../components/AiSnippets/AiSnippets";
import { Variables } from "../../components/Variables/Variables";
import { Spintax, SpintaxHandle } from "../../components/Spintax/Spintax";
import { FeaturesCodes } from "../../../Users/enum/codes";
import { LinkedinInviteStepPanel } from "../../../List/components/CampaignOverview/components/InviteStep/LinkedinInviteStepPanel";
import { SendTestMessageModal } from "../../../List/components/CampaignOverview/components/SendTestMessageModal";
import { resolveEmailSubject } from "../../../List/components/CampaignOverview/utils/resolveEmailSubject";
import { NodePanelHeader } from "../../../List/components/CampaignOverview/components/NodePanelHeader";
import { TestMessageBanner } from "../../../List/components/CampaignOverview/components/TestMessageBanner";

import {
	isNodeMessageChanged,
	replaceNodeMessage,
	resolveNodeMessageField,
} from "./utils/applyNodeMessageEdit";
import { resolveSentStepMessage, resolveSentStepSubject } from "./utils/resolveSentStepContent";
import { resolveNodeScheduleState } from "./utils/resolveNodeScheduleState";

export type StarterNode = Node<NodeData, "StarterNode">;

export type MyNode = Node<NodeData, "emailNode">;

const getCustomCampaignName = (name?: string): string => {
	if (!name) {
		return "Custom Sequence";
	}

	const hasCustomMarker = name.includes("(Custom)") || name.includes("Custom Sequence");

	return hasCustomMarker ? name : `${name} (Custom)`;
};

export const CampaignMessagingOverview = ({
	campaignConfigRaw,
	setIsDirty,
	isDirty,
	setCustomCampaignConfig,
	listPerson,
	textOnly = false,
}: {
	campaignConfigRaw?: CopywritingCampaign | null;
	setIsDirty: (isDirty: boolean) => void;
	listPerson: ListPerson;
	isDirty: boolean;
	setCustomCampaignConfig: (campaignConfig: CopywritingCampaign) => void;
	/**
	 * When true (Review to send), the sequence STRUCTURE is locked — no adding/removing/connecting
	 * nodes — and only the message TEXT can be edited. Custom sequence building is disabled here;
	 * everything beyond text goes through Chat.
	 */
	textOnly?: boolean;
}) => {
	const { user } = useAppSelector(authSelector);
	const { eventBasedState } = useGlobalNodesData();
	const { data: sendingUsers } = useGetSendersUsersQuery();

	const isAiSnippetActive = useIsFeatureActive(FeaturesCodes.AI_SNIPPET);

	// Layer 3: prospect-level sender overrides > campaign config senders > list-level campaign senders
	const effectiveSenders = useMemo<Sender[] | undefined>(() => {
		const resolved = resolveEffectiveSenders(listPerson?.senders, campaignConfigRaw?.senders);

		if (resolved && resolved.length > 0) {
			return resolved;
		}

		// Fall back to list-level campaign config senders
		const allConfigs = listPerson?.list?.campaignConfig as
			| Record<string, CopywritingCampaign>
			| undefined;

		if (!allConfigs) {
			return undefined;
		}

		// Try campaignId first
		if (listPerson?.campaignId && allConfigs[listPerson.campaignId]?.senders) {
			return allConfigs[listPerson.campaignId].senders;
		}

		// Try default campaign
		const defaultKey = Object.keys(allConfigs).find(
			(k) => allConfigs[k].isDefault && allConfigs[k].isValid
		);

		if (defaultKey && allConfigs[defaultKey]?.senders) {
			return allConfigs[defaultKey].senders;
		}

		// Try any valid campaign with senders
		const anyKey = Object.keys(allConfigs).find(
			(k) => allConfigs[k].isValid && allConfigs[k].senders?.length
		);

		return anyKey ? allConfigs[anyKey].senders : undefined;
	}, [
		listPerson?.senders,
		campaignConfigRaw?.senders,
		listPerson?.campaignId,
		listPerson?.list?.campaignConfig,
	]);

	const [sawInfoModal, setSawInfoModal] = useLocalStorageState("sawInfoModal", {
		defaultValue: false,
	});

	const {
		renderModal,
		renderConditionalChoiceModal,
		setShowConditionalChoice,
		setShowNewNodeModal,
	} = useNewNodeConditionModal();

	const { inbox, currentLeadIndex } = useAppSelector(listSelector);

	// PREC-4299: a step added to this lead's sequence starts like one added in the campaign builder.
	useGetSendDelayDefaultsQuery();
	const sendDelayDefaults = useAppSelector(sendDelayDefaultsSelector);
	const nextHasNextNodeRef = useRef<boolean>(false);
	const nextHasNextYesNodeRef = useRef<boolean>(false);
	const nextHasNextNoNodeRef = useRef<boolean>(false);

	useEffect(() => {
		setIsDirty(false);
	}, [currentLeadIndex]);

	const handleAddStep = useCallback(
		({
			position,
			initiatorId,
			edgeType,
			nodeType,
			hasNextNode,
			hasNextYesNode,
			hasNextNoNode,
		}: handleAddStepProps) => {
			setShowNewNodeModal(true);
			newNodePositionRef.current = position;
			newNodeInitiatorIdRef.current = initiatorId || null;

			nextEdgeTypeRef.current = edgeType || "normalEdge";
			nextNodeTypeRef.current = nodeType || "emailNode";

			nextHasNextNodeRef.current = hasNextNode || false;
			nextHasNextYesNodeRef.current = hasNextYesNode || false;
			nextHasNextNoNodeRef.current = hasNextNoNode || false;
		},
		[]
	);

	const handleSetDays = useCallback((nodeId: string, days: number | null) => {
		setIsDirty(true);
		nodesRef.current = nodesRef.current.map((node) => {
			if (node.id === nodeId) {
				node.data.days = days;
			}

			return node;
		});

		forceGraphRerender((v) => v + 1);
	}, []);

	const initialNodes = useMemo(
		() => [
			{
				id: "n1",
				position: { x: -225, y: 0 },
				data: {
					id: 1,
					label: "Start oder so",
					handleAddStep: () =>
						handleAddStep({
							position: { x: 0, y: 0 },
							initiatorId: "n1",
							edgeType: "normalEdge",
							nodeType: "starterNode",
							hasNextNode: false,
						}),
					level: 0,
				},
				draggable: false,
				type: "starterNode",
			},
		],
		[]
	);

	useEffect(() => {
		if (!campaignConfigRaw?.initialNodes) {
			return;
		}

		highestNodeIdRef.current = Math.max(
			...campaignConfigRaw?.initialNodes?.map((node: any) => {
				const idAsNumber = parseInt(node.id.replace("n", ""));

				if (isNaN(idAsNumber)) {
					return 0;
				}

				return idAsNumber;
			}),
			0
		);
	}, [campaignConfigRaw]);

	const campaignConfig = useMemo(() => {
		return hydrateCampaignGraph({
			nodes: campaignConfigRaw?.initialNodes,
			edges: campaignConfigRaw?.initialEdges,
			fallbackNodes: initialNodes as unknown as MyNode[],
			decorateNodeData: (node) => ({
				...resolveNodeScheduleState(listPerson, node, sendingUsers?.data),
				deleteNode: () => handleDeleteNode(node.id),
				handleAddStep,
				getPreviousNode,
				getChildrenNodes,
				handleSetDays,
			}),
			// Fallback to sourceHandle when `type` is missing in stored data so
			// ReactFlow can resolve the registered edge component (normalEdge/yesEdge/noEdge).
			decorateEdge: (edge) => ({ type: edge.type ?? (edge.sourceHandle as string) }),
		});
	}, [campaignConfigRaw, handleAddStep, listPerson, sendingUsers, initialNodes]);

	const initialEdges: Edge[] = useMemo(() => {
		return campaignConfig?.initialEdges ?? [];
	}, [campaignConfig]);

	const edgesRef = useRef<Edge[]>(initialEdges);
	const [selectedNode, setSelectedNode] = useState<
		| MyNode
		| EmailNodeConfig
		| LinkedinInviteNodeConfig
		| LinkedinNodeConfig
		| LinkedinVisitNodeConfig
		| LinkedinInmailNodeConfig
		| null
	>(null);

	const [isSendTestOpen, setIsSendTestOpen] = useState<boolean>(false);
	const [sendTestInviteVariant, setSendTestInviteVariant] = useState<
		"standard" | "premium" | undefined
	>(undefined);
	const aiSnippetsRef = useRef<AiSnippetsHandle>(null);
	const spintaxRef = useRef<SpintaxHandle>(null);

	const [showRemoveDisconnectedModal, setShowRemoveDisconnectedModal] = useState<boolean>(false);

	// Force a re-render after mutating nodesRef/edgesRef
	const [, forceGraphRerender] = useState<number>(0);

	const newNodePositionRef = useRef<{ x: number; y: number } | null>(null);
	const newNodeInitiatorIdRef = useRef<string | null>(null);

	const highestNodeIdRef = useRef(1);
	const nextEdgeTypeRef = useRef<"yesEdge" | "noEdge" | "normalEdge">("normalEdge");
	const nextNodeTypeRef = useRef<CampaignNodeTypes>("emailNode");

	const nodesRef = useRef<MyNode[]>(campaignConfig?.initialNodes ?? initialNodes);

	// Sync nodesRef/edgesRef synchronously during render whenever the underlying
	// campaign data (campaignConfigRaw) changes (e.g. switching prospects). Doing
	// this in render rather than in a useEffect ensures ReactFlow receives the
	// new nodes/edges on the FIRST render after a switch — otherwise the initial
	// render passes stale refs which can leave ReactFlow in an inconsistent
	// internal state (edges not rendered even after the effect updates the refs).
	const lastSyncedRawRef = useRef<CopywritingCampaign | null | undefined>(campaignConfigRaw);

	if (lastSyncedRawRef.current !== campaignConfigRaw) {
		nodesRef.current = campaignConfig?.initialNodes ?? (initialNodes as MyNode[]);
		edgesRef.current = campaignConfig?.initialEdges ?? [];
		lastSyncedRawRef.current = campaignConfigRaw;
	}

	const hasPrecedingEmailNode = useMemo(() => {
		let previousNode: Node<NodeData> | null = getPreviousNode(selectedNode?.id || "");

		do {
			if (previousNode?.type === "emailNode") {
				return true;
			}

			if (previousNode?.id && getPreviousNode) {
				previousNode = getPreviousNode(previousNode.id);
			} else {
				break;
			}
		} while (previousNode?.type !== "starterNode");

		return false;
	}, [selectedNode]);

	function getPreviousNode(nodeId: string): Node<NodeData> | null {
		const previousNodeEdge = edgesRef.current.find((edge) => edge.target === nodeId);

		if (!previousNodeEdge) {
			return null;
		}

		const previousNode = nodesRef.current.find((node) => node.id === previousNodeEdge.source);

		if (!previousNode) {
			return null;
		}

		return previousNode;
	}

	function getChildrenNodes(nodeId: string): Node<NodeData>[] | null {
		const childrenNodesEdges = edgesRef.current.filter((edge) => edge.source === nodeId);

		if (!childrenNodesEdges) {
			return null;
		}

		const childrenNode = nodesRef.current.filter((node) =>
			childrenNodesEdges.some((edge) => edge.target === node.id)
		);

		if (!childrenNode) {
			return null;
		}

		return childrenNode;
	}

	function handleDeleteNode(nodeId: string) {
		const connectingEdges = edgesRef.current.filter(
			(edge) => edge.source === nodeId || edge.target === nodeId
		);

		const removeEdgeIds = connectingEdges.map((edge) => edge.id);

		let incoming: Edge | null = null;
		let outgoing: Edge | null = null;

		for (const edge of connectingEdges) {
			if (edge.target === nodeId) {
				incoming = edge;
			}

			if (edge.source === nodeId) {
				outgoing = edge;
			}
		}

		let newEdge: Edge | null = null;

		if (incoming && outgoing) {
			newEdge = {
				id: `e${incoming.source}-${outgoing.target}`,
				source: incoming.source,
				target: outgoing.target,
				type: incoming?.type ?? outgoing?.type ?? "normalEdge",
				sourceHandle: incoming?.sourceHandle ?? outgoing?.sourceHandle ?? "normalEdge",
			};
		}

		const newNodes = nodesRef.current.filter((node) => node.id !== nodeId);

		nodesRef.current = newNodes;

		const remainingEdges = edgesRef.current.filter((edge) => !removeEdgeIds.includes(edge.id));

		edgesRef.current = newEdge ? [...remainingEdges, newEdge] : remainingEdges;

		nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

		// Force React to re-render so ReactFlow receives updated nodes/edges
		forceGraphRerender((v) => v + 1);
		setIsDirty(true);
	}

	const onNodesChange = useCallback((changes: any) => {
		nodesRef.current = applyNodeChanges(changes, nodesRef.current);
	}, []);
	const onEdgesChange = useCallback((changes: any) => {
		edgesRef.current = applyEdgeChanges(changes, edgesRef.current);
	}, []);

	const closeNewNodeModal = useCallback(() => {
		setShowNewNodeModal(false);
	}, []);

	const handleSetWaitUntil = useCallback((nodeId: string, waitUntil: boolean) => {
		nodesRef.current = nodesRef.current.map((node) => {
			if (node.id === nodeId) {
				node.data.waitUntil = waitUntil;
			}

			return node;
		});

		forceGraphRerender((v) => v + 1);
	}, []);

	const handleSetConditionalValue = useCallback(
		(
			nodeId: string,
			value: { property: string; value: string; title: string; currentType?: string }
		) => {
			nodesRef.current = nodesRef.current.map((node) => {
				if (node.id === nodeId) {
					node.data.conditionalValue = value;
				}

				return node;
			});

			forceGraphRerender((v) => v + 1);
		},
		[]
	);

	function handleAddConditionalNode(
		edgeType?: "yesEdge" | "noEdge" | "normalEdge",
		type?: ConditionalNodeKind
	) {
		setShowConditionalChoice(false);
		const newInitiatorNumber = highestNodeIdRef.current + 1;
		const newId = "n" + newInitiatorNumber;

		const position = newNodePositionRef.current;
		const initiatorId = newNodeInitiatorIdRef.current;

		highestNodeIdRef.current = newInitiatorNumber;

		let level = 0;

		if (!position) {
			return;
		}

		if (!initiatorId) {
			return;
		}

		let connectingNodeOfInitiator: Edge | null = null;

		if (initiatorId) {
			const initiatorNode = nodesRef.current.find((node) => node.id === initiatorId);

			level = (initiatorNode?.data.level || 0) + 1;

			connectingNodeOfInitiator =
				edgesRef.current.find(
					(edge) => edge.source === initiatorId && edge.type === nextEdgeTypeRef.current
				) || null;
		}

		const conditionalValue = buildConditionalValue(type);

		const newNode: Node<NodeData> = {
			id: newId,
			type: "conditionalNode",
			position: { x: 0, y: position.y + 200 },
			data: {
				id: newInitiatorNumber,
				label: `Node ${newId}`,
				handleAddStep: handleAddStep,
				source: initiatorId,
				deleteNode: handleDeleteNode,
				level: level,
				getPreviousNode: getPreviousNode,
				getChildrenNodes: getChildrenNodes,
				edgeType: nextEdgeTypeRef.current as unknown as typeof edgeTypes,
				nodeType: nextNodeTypeRef.current as unknown as typeof nodeTypes,
				days: DEFAULT_NEW_STEP_DAYS,
				handleSetDays: handleSetDays,
				handleSetWaitUntil: handleSetWaitUntil,
				handleSetConditionalValue,
				conditionalValue,
			},
			draggable: false,
		};

		nodesRef.current = [...nodesRef.current, newNode as MyNode];

		if (initiatorId) {
			const newEdge: Edge = {
				id: `e${initiatorId}-${newId}`,
				source: initiatorId,
				target: newId,
				type: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
				sourceHandle: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
			};

			let newEdges = [...edgesRef.current, newEdge];

			if (connectingNodeOfInitiator) {
				const secondEdge: Edge = {
					id: `e${newId}-${connectingNodeOfInitiator.target}`,
					source: newId,
					target: connectingNodeOfInitiator.target,
					sourceHandle: edgeType || "yesEdge",
					type: edgeType || "yesEdge",
				};

				newEdges = [
					...newEdges.filter((edge) => edge.id !== connectingNodeOfInitiator?.id),
					secondEdge,
				];
			}

			edgesRef.current = [...newEdges];
		}

		nextEdgeTypeRef.current = "normalEdge";

		nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

		// Force React to re-render so ReactFlow receives updated nodes/edges
		forceGraphRerender((v) => v + 1);

		closeNewNodeModal();
	}

	const handleChangeNodeType = useCallback((nodeId: string, nodeType: CampaignNodeTypes) => {
		const node = nodesRef.current.find((node) => node.id === nodeId) as Node<NodeData>;

		if (!node) {
			return;
		}

		node.type = nodeType;
		// A step that becomes an invite is a new invite step (PREC-4426); an invite keeps its switch.
		node.data = { ...newStepDataFor(nodeType), ...node.data };

		nodesRef.current = [...nodesRef.current];

		forceGraphRerender((v) => v + 1);
		setSelectedNode(null);
		setSelectedNode(node as unknown as MyNode);
	}, []);

	function handleAddEmailNode(type: CampaignNodeTypes) {
		setIsDirty(true);
		const newInitiatorNumber = highestNodeIdRef.current + 1;
		const newId = "n" + newInitiatorNumber;

		const position = newNodePositionRef.current;

		newNodePositionRef.current = null;

		const initiatorId = newNodeInitiatorIdRef.current;

		newNodeInitiatorIdRef.current = null;

		highestNodeIdRef.current = newInitiatorNumber;

		let level = 0;

		if (!position) {
			return;
		}

		if (!initiatorId) {
			return;
		}

		let connectingNodeOfInitiator: Edge | null = null;

		if (initiatorId) {
			const initiatorNode = nodesRef.current.find((node) => node.id === initiatorId);

			level = (initiatorNode?.data.level || 0) + 1;

			connectingNodeOfInitiator =
				edgesRef.current.find(
					(edge) => edge.source === initiatorId && edge.type === nextEdgeTypeRef.current
				) || null;
		}

		const newNode: Node<NodeData> = {
			id: newId,
			type: type,
			position: { x: 0, y: position.y + 200 },
			data: {
				id: newInitiatorNumber,
				label: `Node ${newId}`,
				handleAddStep: handleAddStep,
				source: initiatorId,
				deleteNode: handleDeleteNode,
				level: level,
				getPreviousNode: getPreviousNode,
				getChildrenNodes: getChildrenNodes,
				edgeType: nextEdgeTypeRef.current as unknown as typeof edgeTypes,
				nodeType: nextNodeTypeRef.current as unknown as typeof nodeTypes,
				days: resolveNewStepDays({
					nodeType: type,
					parentId: initiatorId,
					nodes: nodesRef.current,
					edges: edgesRef.current,
					sendDelayDefaults,
				}),
				handleSetDays: handleSetDays,
				...newStepDataFor(type),
			},
			draggable: false,
		};

		nodesRef.current = [...nodesRef.current, newNode as MyNode];

		if (initiatorId) {
			const newEdge: Edge = {
				id: `e${initiatorId}-${newId}`,
				source: initiatorId,
				target: newId,
				type: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
				sourceHandle: connectingNodeOfInitiator?.sourceHandle || nextEdgeTypeRef.current,
			};

			let newEdges = [...edgesRef.current, newEdge];

			if (connectingNodeOfInitiator) {
				const secondEdge: Edge = {
					id: `e${newId}-${connectingNodeOfInitiator.target}`,
					source: newId,
					target: connectingNodeOfInitiator.target,
					sourceHandle: "normalEdge",
					type: "normalEdge",
				};

				newEdges = [
					...newEdges.filter((edge) => edge.id !== connectingNodeOfInitiator?.id),
					secondEdge,
				];
			}

			edgesRef.current = [...newEdges];
		}

		nextEdgeTypeRef.current = "normalEdge";

		nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

		// Force React to re-render so ReactFlow receives updated nodes/edges
		forceGraphRerender((v) => v + 1);

		closeNewNodeModal();

		setSelectedNode(newNode as MyNode);
	}

	useEffect(() => {
		const analysis = analyzeDisconnectedNodes(nodesRef.current, edgesRef.current);
		const orphanRootSet = new Set(analysis.orphanRootIds);
		const openNormalSet = new Set(
			analysis.openSources.filter((s) => s.handle === "normalEdge").map((s) => s.nodeId)
		);
		const openYesSet = new Set(
			analysis.openSources.filter((s) => s.handle === "yesEdge").map((s) => s.nodeId)
		);
		const openNoSet = new Set(
			analysis.openSources.filter((s) => s.handle === "noEdge").map((s) => s.nodeId)
		);

		const newNodes = nodesRef.current.map((node) => {
			const source = edgesRef.current.find((edge) => edge.target === node.id)?.source;
			const target = edgesRef.current.find((edge) => edge.source === node.id)?.target;

			const isDisconnected =
				(node as Node).type === "starterNode" && analysis.starterDisconnected ? true : false;

			const isDisconnectedTarget = orphanRootSet.has(node.id);

			const hasOpenSource = !isDisconnected && openNormalSet.has(node.id);
			const hasOpenYesSource = openYesSet.has(node.id);
			const hasOpenNoSource = openNoSet.has(node.id);

			return {
				...node,
				data: {
					...node.data,
					target: target,
					source: source,
					isDisconnected,
					isDisconnectedTarget,
					hasOpenSource,
					hasOpenYesSource,
					hasOpenNoSource,
				},
			};
		});

		if (JSON.stringify(newNodes) !== JSON.stringify(nodesRef.current)) {
			nodesRef.current = newNodes;
		}
	}, [nodesRef.current, edgesRef.current]);

	const onConnect = useCallback((connection: Connection) => {
		if (connection.source === connection.target) {
			return;
		}

		const analysis = analyzeDisconnectedNodes(nodesRef.current, edgesRef.current);

		if (analysis.orphanedNodeIds.length === 0) {
			return;
		}

		const handle =
			(connection.sourceHandle as "normalEdge" | "yesEdge" | "noEdge" | null) ?? "normalEdge";
		const isOpenSource = analysis.openSources.some(
			(slot) => slot.nodeId === connection.source && slot.handle === handle
		);
		const isOrphanRoot = analysis.orphanRootIds.includes(connection.target ?? "");

		if (!isOpenSource || !isOrphanRoot) {
			return;
		}

		const newEdge: Edge = {
			id: `e${connection.source}-${connection.target}`,
			source: connection.source as string,
			target: connection.target as string,
			type: handle,
			sourceHandle: handle,
		};

		edgesRef.current = [...edgesRef.current, newEdge];
		nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

		forceGraphRerender((v) => v + 1);
		setIsDirty(true);
	}, []);

	const handleRemoveDisconnectedNodes = useCallback(() => {
		const analysis = analyzeDisconnectedNodes(nodesRef.current, edgesRef.current);

		if (analysis.orphanedNodeIds.length === 0) {
			return;
		}

		const orphanedSet = new Set(analysis.orphanedNodeIds);

		nodesRef.current = nodesRef.current.filter((node) => !orphanedSet.has(node.id));
		edgesRef.current = edgesRef.current.filter(
			(edge) => !orphanedSet.has(edge.source) && !orphanedSet.has(edge.target)
		);

		nodesRef.current = autoLayoutHorizontal(nodesRef.current, edgesRef.current);

		forceGraphRerender((v) => v + 1);
		setIsDirty(true);
	}, []);

	const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
		if (node.type !== "starterNode" && node.type !== "conditionalNode") {
			setSelectedNode(node as MyNode);
		} else {
			setSelectedNode(null);
		}
	}, []);

	const prospect = useMemo(() => {
		return inbox[currentLeadIndex]?.prospect;
	}, [inbox, currentLeadIndex]);

	const suggestions = useMemo(
		() => (inbox[currentLeadIndex] ? getSuggestions(inbox[currentLeadIndex]) : []),
		[inbox, currentLeadIndex]
	);

	// The list's event-outreach row: what makes {event_*} and the custom tokens exist for the
	// editors, their `{` autocompleter, the footer and the validity check (PREC-4010).
	const listEvent = useMemo(
		() => inbox[currentLeadIndex]?.list?.event ?? listPerson?.list?.event,
		[inbox, currentLeadIndex, listPerson]
	);

	const {
		isValid: isValidCampaignConfig,
		errors: campaignErrors,
		warnings: campaignWarnings,
	} = useMemo(() => {
		function getPreviousNode(nodeId: string): Node<NodeData> | null {
			const previousNodeEdge = edgesRef.current.find((edge) => edge.target === nodeId);

			if (!previousNodeEdge) {
				return null;
			}

			const previousNode = nodesRef.current.find((node) => node.id === previousNodeEdge.source);

			if (!previousNode) {
				return null;
			}

			return previousNode;
		}

		let isValid = true;
		const errors: string[] = [];
		const warnings: string[] = [];

		if (!nodesRef.current || nodesRef.current.length <= 1) {
			isValid = false;
			errors.push("Campaign must have at least 1 node");
		}

		if (!isGraphConnected(nodesRef.current, edgesRef.current)) {
			isValid = false;
			const analysis = analyzeDisconnectedNodes(nodesRef.current, edgesRef.current);

			if (analysis.starterDisconnected) {
				errors.push("Start node is disconnected — drag to reconnect");
			} else {
				errors.push("Some nodes are disconnected — drag to reconnect or remove them");
			}
		}

		let hasEmailNode = false;
		let hasLinkedinNode = false;

		for (const node of nodesRef.current) {
			if (node.type === "emailNode") {
				hasEmailNode = true;
				let hasPrecedingEmailNode = false;
				let previousNode: Node<NodeData> | null = getPreviousNode(node.id);

				do {
					if (previousNode?.type === "emailNode") {
						hasPrecedingEmailNode = true;
						break;
					}

					if (previousNode?.id && getPreviousNode) {
						previousNode = getPreviousNode(previousNode.id);
					} else {
						break;
					}
				} while (previousNode?.type !== "starterNode");

				if (!node.data.subject && !hasPrecedingEmailNode && !node.data.message) {
					isValid = false;
					errors.push("Email node must have a subject and message");
				}

				if (!node.data.subject && !hasPrecedingEmailNode && node.data.message) {
					isValid = false;
					errors.push("Email node must have a subject");
				}

				if ((node.data.subject || hasPrecedingEmailNode) && !node.data.message) {
					isValid = false;
					errors.push("Email node must have a message");
				}
			} else if (node.type === "linkedinNode") {
				hasLinkedinNode = true;
				if (!node.data.message) {
					isValid = false;
					errors.push("Linkedin node must have a message");
				}
			}
		}

		// Check for invalid placeholders in node messages and subjects
		// Use both nodesRef.current (live edits) and campaignConfigRaw?.initialNodes (saved data)
		// to handle the race condition where nodesRef might be stale
		const validKeys = inbox[currentLeadIndex]
			? getSuggestions(inbox[currentLeadIndex], true, undefined, listEvent)
			: getSuggestions(undefined, true, listPerson?.list?.columns, listEvent);

		const nodeSourcesToCheck = [
			nodesRef.current,
			...(campaignConfigRaw?.initialNodes ? [campaignConfigRaw.initialNodes] : []),
		];

		// Invalid variables still invalidate the campaign, but are deliberately not
		// pushed into the error overlay here: the Outbox check runs against the
		// currently selected lead, so they surface in the campaign builder (and as
		// red pills in the editors) instead (PREC-3889).
		if (collectInvalidVariableErrors(nodeSourcesToCheck, validKeys).length > 0) {
			isValid = false;
		}

		const emailConflict = prospect && !prospect.email && hasEmailNode;
		const linkedinConflict = prospect && !prospect.linkedinUrl && hasLinkedinNode;

		if (
			emailConflict &&
			inbox[currentLeadIndex].list.getEmailInformation !==
				GetEmailInformation.WHEN_TRIED_TO_SEND_EMAIL
		) {
			warnings.push(
				"Selected lead does not have an email address, but the campaign has an email node"
			);
		}

		if (linkedinConflict) {
			warnings.push(
				"Selected lead does not have a LinkedIn URL, but the campaign has a LinkedIn message or invite node"
			);
		}

		// Check if {signature} is used but any active email sender lacks a signature
		if (nodesContainSignatureVariable(nodesRef.current)) {
			const activeEmailSenders =
				effectiveSenders?.filter((sender: Sender) => sender.active && sender.type === "email") ??
				[];

			const allEmails = sendingUsers?.data?.flatMap((user) => user.emails ?? []) ?? [];

			const sendersWithoutSignature = activeEmailSenders.filter((sender: Sender) => {
				const email = allEmails.find((e) => e.id === sender.emailId);

				return !email?.signature;
			});

			if (sendersWithoutSignature.length > 0) {
				isValid = false;
				const missingEmails = sendersWithoutSignature.map((s: Sender) => s.emailAddress).join(", ");

				errors.push(
					`All sender emails must have a signature when using the {signature} variable (missing: ${missingEmails})`
				);
			}
		}

		return { isValid, errors, warnings };
	}, [
		nodesRef.current,
		edgesRef.current,
		prospect,
		listPerson,
		inbox,
		currentLeadIndex,
		campaignConfigRaw,
		effectiveSenders,
		sendingUsers,
		listEvent,
	]);

	const handleNodeMessageChange = (value: string) => {
		const field = resolveNodeMessageField(eventBasedState);

		setSelectedNode({
			...selectedNode,
			data: { ...selectedNode?.data, [field]: value },
		} as EmailNodeConfig);
		nodesRef.current = replaceNodeMessage(nodesRef.current, selectedNode?.id, field, value);
		forceGraphRerender((v) => v + 1);

		if (isNodeMessageChanged(selectedNode, field, value)) {
			setIsDirty(true);
		}
	};

	const handlePotentialUpdate = () => {
		const uuid = uuidv4();

		setCustomCampaignConfig({
			...campaignConfigRaw,
			name: getCustomCampaignName(campaignConfigRaw?.name),
			initialNodes: nodesRef.current,
			initialEdges: edgesRef.current,
			id: campaignConfigRaw?.id || uuid,
			isDefault: false,
			isValid: isValidCampaignConfig,
			timezone: campaignConfigRaw?.timezone || user?.timezone || "",
			schedule: campaignConfigRaw?.schedule || {
				monday: {
					active: true,
					fromTime: "08:00",
					toTime: "17:00",
				},
				tuesday: {
					active: true,
					fromTime: "08:00",
					toTime: "17:00",
				},
				wednesday: {
					active: true,
					fromTime: "08:00",
					toTime: "17:00",
				},
				thursday: {
					active: true,
					fromTime: "08:00",
					toTime: "17:00",
				},
				friday: {
					active: true,
					fromTime: "08:00",
					toTime: "17:00",
				},
				saturday: {
					active: false,
					fromTime: "08:00",
					toTime: "17:00",
				},
				sunday: {
					active: false,
					fromTime: "08:00",
					toTime: "17:00",
				},
			},
		});

		setIsDirty(true);
	};

	useEffect(() => {
		if (isDirty) {
			handlePotentialUpdate();
		}
	}, [nodesRef.current, isDirty]);

	// Persists message updates (AI snippets / variables panel inserts) into the
	// selected node and the node graph, and marks the campaign dirty.
	const persistSelectedNodeMessages = (results: string[]) => {
		if (!selectedNode) {
			return;
		}

		const updates =
			selectedNode.type === "linkedinInviteNode"
				? eventBasedState
					? {
							alternatePremiumMessage: results[0],
							alternateMessage: results[1],
						}
					: { premiumMessage: results[0], message: results[1] }
				: eventBasedState
					? { alternateMessage: results?.[0] }
					: { message: results?.[0] };

		setSelectedNode({
			...selectedNode,
			data: {
				...selectedNode?.data,
				...updates,
			},
		} as EmailNodeConfig);

		nodesRef.current = nodesRef.current.map((node) => {
			if (node.id === selectedNode?.id) {
				return { ...node, data: { ...node.data, ...updates } };
			}

			return node;
		});

		setIsDirty(true);
	};

	return (
		<div className="flex flex-col justify-between items-start w-full h-[100%] bg-white">
			<div className="flex w-full h-full flex-row relative">
				{campaignErrors.length > 0 && (
					<div className="absolute top-0 left-0 w-full h-fit flex items-start justify-start z-[450] bg-black/50 p-2">
						<div className="flex flex-row flex-wrap gap-1 items-center">
							{campaignErrors.map((error) => (
								<span
									key={error}
									className="whitespace-nowrap text-sm text-status-error bg-status-error-light rounded-full p-1 px-2 flex items-center gap-1"
								>
									{error}
									{error.includes("remove them") && (
										<button
											className="ml-1 text-xs font-semibold text-white bg-status-error rounded-full px-2 py-0.5 hover:opacity-80"
											onClick={() => setShowRemoveDisconnectedModal(true)}
										>
											Remove
										</button>
									)}
								</span>
							))}
						</div>
					</div>
				)}
				{campaignWarnings.length > 0 && (
					<div className="absolute top-0 left-0 w-full h-fit flex items-start justify-start z-[450] bg-black/50 p-2">
						<div className="flex flex-row flex-wrap gap-1">
							{campaignWarnings.map((warning) => (
								<span
									key={warning}
									className="whitespace-nowrap text-sm text-status-error bg-status-warning-light rounded-full p-1 px-2"
								>
									{warning}
								</span>
							))}
						</div>
					</div>
				)}
				<div
					className={classNames(
						"flex bg-brand-white h-full w-full relative transition-all duration-300"
					)}
				>
					<ReactFlow
						defaultViewport={{ x: 500, y: 100, zoom: 1 }}
						deleteKeyCode={textOnly ? null : undefined}
						edgeTypes={edgeTypes}
						edges={edgesRef.current}
						nodeTypes={nodeTypes}
						nodesConnectable={!textOnly}
						nodesDraggable={!textOnly}
						// In text-only mode strip the structural affordances (add-step / delete buttons render
						// only when these callbacks are present on the node data) so only message text is editable.
						nodes={
							textOnly
								? nodesRef.current.map((node) => ({
										...node,
										draggable: false,
										data: { ...node.data, handleAddStep: undefined, deleteNode: undefined },
									}))
								: nodesRef.current
						}
						snapGrid={[10, 10]}
						snapToGrid={true}
						onClick={() => {
							setSelectedNode(null);
						}}
						onConnect={textOnly ? undefined : onConnect}
						onEdgesChange={textOnly ? undefined : onEdgesChange}
						onNodeClick={(e, node) => {
							e.stopPropagation();
							e.preventDefault();

							setSelectedNode(null);
							setTimeout(() => {
								onNodeClick(e, node);
							}, 1);
						}}
						onNodesChange={onNodesChange}
					>
						<Background variant={BackgroundVariant.Dots} />
						<Controls />
					</ReactFlow>
				</div>
				{!sawInfoModal && (
					<div className="absolute bottom-0 left-0 w-full h-fit flex items-start justify-start z-[450] bg-white p-2 w-full border-t border-border-light">
						<div className="w-full max-w-[calc(100%-50px)] relative">
							<Icon
								className="w-4 h-4 text-text-disabled cursor-pointer absolute top-0 right-0 bg-brand-white rounded-full p-1 border border-border-light"
								icon={faX}
								onClick={() => setSawInfoModal(true)}
							/>
							<div className="flex flex-row flex-wrap gap-1 text-sm text-text-meta">
								<span className="text-text-meta text-xs px-2 py-1 bg-gray-100 rounded-full border border-border-light">
									Sequences stop automatically as soon as the lead responds to a message.
								</span>
								<span className="text-text-meta text-xs px-2 py-1 bg-gray-100 rounded-full border border-border-light">
									If a step fails, the next step will be executed
								</span>
							</div>
						</div>
					</div>
				)}
				{selectedNode && (
					<div className="absolute top-0 right-[50px] w-1/4 z-[50000] min-w-[400px] h-[100%] bg-white border-l border-t border-gray-200 rounded-tl-xl p-4 overflow-y-auto scrollbar-hide">
						<NodePanelHeader
							disabled={selectedNode.data.alreadySent}
							level={selectedNode.data.level}
							nodeType={selectedNode.type as CampaignNodeTypes}
							selectedSenders={
								(selectedNode.data?.senders as DropdownWithCheckboxesItem[]) || undefined
							}
							senders={effectiveSenders}
							onChangeNodeType={(nodeType) => {
								handleChangeNodeType(selectedNode.id, nodeType);
							}}
							onChangeSenders={(senders) => {
								setSelectedNode({
									...selectedNode,
									data: {
										...selectedNode?.data,
										senders,
									},
								} as unknown as MyNode);

								nodesRef.current = nodesRef.current.map((node) => {
									if (node.id === selectedNode?.id) {
										return { ...node, data: { ...node.data, senders } };
									}

									return node;
								});

								setIsDirty(true);
							}}
						/>
						{["emailNode", "linkedinInmailNode"].includes(selectedNode.type) && (
							<div className="w-full">
								{!selectedNode.data.alreadySent ? (
									<div className="w-full">
										<CampaignEmailSubjectEditor
											nodeId={selectedNode.id}
											nodeType={selectedNode.type}
											subject={
												eventBasedState
													? (selectedNode?.data.alternateSubject ?? "")
													: (selectedNode?.data.subject ?? "")
											}
											hasPrecedingEmailNode={hasPrecedingEmailNode}
											eventBasedState={eventBasedState}
											getPreviousNode={getPreviousNode}
											currentLeadIndex={currentLeadIndex}
											listColumns={listPerson?.list?.columns}
											onChange={(value: string) => {
												setSelectedNode({
													...selectedNode,
													data: setSubjectOnNodeData(selectedNode.data, value, eventBasedState),
												} as unknown as MyNode);

												nodesRef.current = nodesRef.current.map((node) =>
													node.id === selectedNode?.id
														? {
																...node,
																data: setSubjectOnNodeData(node.data, value, eventBasedState),
															}
														: node
												);

												forceGraphRerender((v) => v + 1);

												const previousSubject = eventBasedState
													? selectedNode?.data.alternateSubject
													: selectedNode?.data.subject;

												if (value !== previousSubject) {
													setIsDirty(true);
												}
											}}
											onInputChange={(value: boolean) => {
												void value;
												setIsDirty(true);
											}}
										/>
									</div>
								) : (
									<div
										dangerouslySetInnerHTML={{
											__html: resolveSentStepSubject(
												selectedNode.data,
												eventBasedState,
												inbox[currentLeadIndex],
												suggestions
											),
										}}
									/>
								)}
							</div>
						)}

						<div className="w-full pb-28">
							{!selectedNode.data.alreadySent && selectedNode.type === "emailNode" ? (
								<>
									<EmailEditor
										listColumns={listPerson?.list?.columns}
										listEvent={listEvent}
										listId={inbox[currentLeadIndex]?.list?.id}
										showAiSnippetButton={isAiSnippetActive}
										onCreateAiSnippet={
											isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
										}
										onCreateSpintax={() => spintaxRef.current?.openCreateModal()}
										currentLeadIndex={currentLeadIndex}
										handleChange={handleNodeMessageChange}
										handleInputChange={() => {
											setIsDirty(true);
										}}
										placeholder="Hey {prospect_firstName|Prospect First Name}, Write your message here"
										sender={effectiveSenders?.[0]}
										signature={
											eventBasedState
												? (selectedNode?.data.alternateMessage ?? "")
												: (selectedNode?.data.message ?? "")
										}
									/>
								</>
							) : !selectedNode.data.alreadySent &&
							  ["linkedinInviteNode"].includes(selectedNode.type) ? (
								<>
									<LinkedinInviteStepPanel
										currentLeadIndex={currentLeadIndex}
										eventBasedState={eventBasedState}
										listColumns={listPerson?.list?.columns}
										listEvent={listEvent}
										listId={inbox[currentLeadIndex]?.list?.id}
										nodesRef={nodesRef}
										selectedNode={selectedNode}
										sender={effectiveSenders?.[0]}
										setIsDirty={setIsDirty}
										setSelectedNode={setSelectedNode}
										showAiSnippetButton={isAiSnippetActive}
										onCreateAiSnippet={
											isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
										}
										onCreateSpintax={() => spintaxRef.current?.openCreateModal()}
									/>
								</>
							) : !selectedNode.data.alreadySent &&
							  ["linkedinNode", "linkedinInmailNode"].includes(selectedNode.type) ? (
								<>
									<LinkedinEditor
										listColumns={listPerson?.list?.columns}
										listEvent={listEvent}
										listId={inbox[currentLeadIndex]?.list?.id}
										showAiSnippetButton={isAiSnippetActive}
										onCreateAiSnippet={
											isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
										}
										onCreateSpintax={() => spintaxRef.current?.openCreateModal()}
										currentLeadIndex={currentLeadIndex}
										handleChange={handleNodeMessageChange}
										handleInputChange={() => {
											setIsDirty(true);
										}}
										placeholder="Write your message here"
										sender={effectiveSenders?.[0]}
										signature={
											eventBasedState
												? (selectedNode?.data.alternateMessage ?? "")
												: (selectedNode?.data.message ?? "")
										}
									/>
								</>
							) : (
								<div
									dangerouslySetInnerHTML={{
										__html: resolveSentStepMessage(
											selectedNode.data,
											eventBasedState,
											inbox[currentLeadIndex],
											suggestions
										),
									}}
								/>
							)}
							{!selectedNode.data.alreadySent && (
								<TestMessageBanner
									listData={inbox[currentLeadIndex]?.list}
									message={selectedNode.data.message}
									nodeType={selectedNode.type as CampaignNodeTypes}
									premiumMessage={selectedNode.data.premiumMessage}
									onCreateAiSnippets={
										isAiSnippetActive ? () => aiSnippetsRef.current?.openCreateModal() : undefined
									}
									onSendTest={(variant) => {
										setSendTestInviteVariant(variant);
										setIsSendTestOpen(true);
									}}
								/>
							)}
							{isAiSnippetActive &&
								!selectedNode.data.alreadySent &&
								["emailNode", "linkedinNode", "linkedinInmailNode", "linkedinInviteNode"].includes(
									selectedNode.type
								) && (
									<AiSnippets
										ref={aiSnippetsRef}
										listData={inbox[currentLeadIndex].list}
										selectedNode={selectedNode}
										withPersistance={persistSelectedNodeMessages}
									/>
								)}
							{!selectedNode.data.alreadySent &&
								["emailNode", "linkedinNode", "linkedinInmailNode", "linkedinInviteNode"].includes(
									selectedNode.type
								) && (
									<Variables
										listData={inbox[currentLeadIndex]?.list}
										selectedNode={selectedNode}
										withPersistance={persistSelectedNodeMessages}
									/>
								)}
							{!selectedNode.data.alreadySent &&
								["emailNode", "linkedinNode", "linkedinInmailNode", "linkedinInviteNode"].includes(
									selectedNode.type
								) && (
									<Spintax
										ref={spintaxRef}
										listData={inbox[currentLeadIndex]?.list}
										selectedNode={selectedNode}
										withPersistance={persistSelectedNodeMessages}
									/>
								)}
						</div>
					</div>
				)}
				{selectedNode && isSendTestOpen && (
					<SendTestMessageModal
						campaignSenders={effectiveSenders}
						channel={selectedNode.type === "emailNode" ? "email" : "linkedin"}
						inviteVariant={
							selectedNode.type === "linkedinInviteNode" ? sendTestInviteVariant : undefined
						}
						isOpen={isSendTestOpen}
						listPersonId={listPerson?.id ?? inbox[currentLeadIndex]?.id}
						message={selectedNode.data.message ?? ""}
						nodeType={selectedNode.type}
						premiumMessage={
							selectedNode.type === "linkedinInviteNode"
								? (selectedNode.data.premiumMessage ?? "")
								: undefined
						}
						sendingUsers={sendingUsers?.data}
						subject={
							selectedNode.type === "emailNode"
								? resolveEmailSubject(selectedNode.id, getPreviousNode, selectedNode.data.subject)
								: undefined
						}
						onClose={() => {
							setIsSendTestOpen(false);
							setSendTestInviteVariant(undefined);
						}}
					/>
				)}
				{renderModal(
					handleAddEmailNode,
					handleAddConditionalNode,
					nextHasNextNodeRef.current,
					nextNodeTypeRef.current,
					nextEdgeTypeRef.current,
					nextHasNextYesNodeRef.current,
					nextHasNextNoNodeRef.current
				)}
				{renderConditionalChoiceModal(handleAddConditionalNode)}
				{showRemoveDisconnectedModal && (
					<Modal
						handleClose={() => setShowRemoveDisconnectedModal(false)}
						handleSave={() => {
							handleRemoveDisconnectedNodes();
							setShowRemoveDisconnectedModal(false);
						}}
						isOpened={showRemoveDisconnectedModal}
						submitButtonColor={ButtonColor.DANGER}
						submitButtonText="Remove"
						title="Remove disconnected nodes"
					>
						<div className="text-sm">
							This will permanently remove all disconnected nodes from the campaign. This action
							cannot be undone. Are you sure you want to continue?
						</div>
					</Modal>
				)}
			</div>
		</div>
	);
};
